<script setup>
/**
 * AppCharacterCount — "45/80" under a text field, red once the limit is passed.
 *
 *   ┌────────────────────────────────┐
 *   │ Ut enim ad minim veniam, quis  │
 *   └────────────────────────────────┘
 *                             45/80        ← right-aligned, gray
 *   ⊗ Titles can't exceed 80 characters.   ← only when over, only if `message`
 *
 * The count is the primary signal: it's there before you hit the limit, so the
 * field explains itself while you type rather than only after you've broken a
 * rule. The message is optional for surfaces that carry the explanation
 * elsewhere (the grid cell shows it in a floating popover instead).
 *
 * Going over is NOT blocked here — no `maxlength` — because the limit belongs to
 * one marketplace, and truncating someone's title silently is worse than
 * telling them it's too long.
 */
import { computed } from 'vue'

const props = defineProps({
  /** Current length. Pass `value.length` — the field owns the value. */
  length: { type: Number, required: true },
  max:    { type: Number, required: true },
  /** Shown, in red, only once over the limit. Empty = counter only. */
  message: { type: String, default: '' },
})

const isOver = computed(() => props.length > props.max)
</script>

<template>
  <div class="flex flex-col gap-y-1">
    <span
      class="self-end text-xs leading-none tabular-nums"
      :class="isOver ? 'text-red-500' : 'text-gray-400'"
    >{{ length }}/{{ max }}</span>

    <p v-if="isOver && message" class="flex items-center gap-x-1 text-xs text-red-500">
      <Icon icon="heroicons:exclamation-circle" class="shrink-0 text-sm" aria-hidden="true" />
      {{ message }}
    </p>
  </div>
</template>
